import styled from '@emotion/styled'
import * as React from 'react'

import { LinguiMessageDescriptor, useTrans } from '~logic/i18n/useTrans'
import {
  AgeControlMessageBox,
} from '~shared/components/Messages/AgeControlMessageBox'
import { TColorKey } from '~theme/typings'
import {
  FullScreenDrawer,
} from '~ui/components/content/Drawer/FullScreenDrawer'
import { Dropin } from '~ui/components/content/Dropin/Dropin'
import { ModalBody } from '~ui/components/content/Modal/components/ModalBody'
import {
  ModalFooter,
} from '~ui/components/content/Modal/components/ModalFooter'
import {
  ModalHeader,
} from '~ui/components/content/Modal/components/ModalHeader'
import { Modal } from '~ui/components/content/Modal/Modal'
import { IconLine } from '~ui/components/IconLine/IconLine'
import { IconCart } from '~ui/components/icons/specific/IconCart'
import { Flex, Grid } from '~ui/components/layout/Box'
import { Text } from '~ui/components/typography/Text/Text'
import { getSpacingValue, TSingleSpace } from '~ui/styleGuide/spaces'

import { useCartContentCtx } from '../contexts/CartContentProvider'
import { CartTotal } from './CartTotal'
import { OrderPriceMessage } from './OrderPriceMessage'

const modalBodyPadding: TSingleSpace = `medium`

/**
 * Sets a negative margin for the container
 * so that the promo category is rendered from edge to edge of the modal.
 */
const StyledFullWidthContainer = styled(Dropin)`
  --cart-content-margin-value: ${getSpacingValue(modalBodyPadding)};

  flex-grow: 1;

  margin-top: 1px; // to stay below the cart divider
  margin-left: calc(-1 * var(--cart-content-margin-value));
  margin-right: calc(-1 * var(--cart-content-margin-value));
  margin-bottom: calc(-1 * var(--cart-content-margin-value));

  &:empty {
    display: none;
  }
`

type TDrawerProps = Pick<PropsOf<typeof FullScreenDrawer>, 'isOpen' | 'onClose'>

type TProps = RequiredChildren &
  TDrawerProps & {
    isFullScreen?: boolean
    cartTitle: LinguiMessageDescriptor
    /**
     * such as promo categories, rendered below the cart content on the `card` background
     */
    additionalContent?: React.ReactNode
    /**
     * submit button for the cart
     */
    cartSubmitButton: React.ReactNode
    /**
     * message for the transport fee in the order price message
     */
    transportFeeMessage?: PropsOf<typeof OrderPriceMessage>['transportFeeMessage']
    /**
     * whether the skeleton for the cart total should be shown
     */
    showSkeleton?: PropsOf<typeof CartTotal>['showSkeleton']
    /**
     * credit payment part of the cart total
     */
    creditPayment?: PropsOf<typeof CartTotal>['creditPayment']
  }

const modalBg: TColorKey = `bgColors.primary`
const modalHeaderBg: TColorKey = `bgColors.contrasted`
const modalFooterBg: TColorKey = `bgColors.card`
const drawerFooterBg: TColorKey = `bgColors.primary`

/**
 * Full screen drawer cart OR modal with centered cart,
 * depending on `isFullScreen` prop.
 */
export const CartModal: React.FC<TProps> = ({
  isOpen,
  onClose,
  cartTitle,
  cartSubmitButton,
  additionalContent,
  transportFeeMessage,
  isFullScreen = false,
  showSkeleton = false,
  creditPayment,
  children,
}) => {
  const order = useCartContentCtx()
  const hasAgeControl = order?.ageControl
  const { trans } = useTrans()

  const renderFooterContent = () => {
    return (
      <Grid flexGrow={1} rowGap={`medium`}>
        <OrderPriceMessage transportFeeMessage={transportFeeMessage} />
        <CartTotal showSkeleton={showSkeleton} creditPayment={creditPayment} />
        {cartSubmitButton}
      </Grid>
    )
  }

  const renderCartContent = () => {
    return (
      <Grid gap={hasAgeControl ? `smallMedium` : `none`}>
        {hasAgeControl && <AgeControlMessageBox />}
        {children}
        {additionalContent && (
          <StyledFullWidthContainer
            bgColor={`bgColors.card`}
            padding={modalBodyPadding}
            shadowInset={`mediumVertical`}
          >
            {additionalContent}
          </StyledFullWidthContainer>
        )}
      </Grid>
    )
  }

  if (isFullScreen) {
    return (
      <FullScreenDrawer
        isOpen={isOpen}
        onClose={onClose}
        bgColor={modalBg}
        containerProps={{
          padding: modalBodyPadding,
          display: `flex`,
          flexDirection: `column`,
        }}
        headerProps={{
          closeIconSide: `right`,
          title: (
            <Text fontStyle={`boldSemi`} fontSize={`m18`}>
              {trans(cartTitle)}
            </Text>
          ),
        }}
        footerProps={{
          bgColor: drawerFooterBg,
          content: renderFooterContent(),
        }}
      >
        {renderCartContent()}
      </FullScreenDrawer>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={`xs`}
      // TODO zIndex
      overlayZIndex={100}
      padding={`none`}
      radius={`small`}
    >
      <ModalHeader
        bgColor={modalHeaderBg}
        shadow={`medium`}
        padding={{ vertical: `smallMedium`, horizontal: `medium` }}
        withCloseButton
      >
        <Grid>
          <IconLine icon={IconCart}>
            <Text fontStyle={`boldSemi`}>{trans(cartTitle)}</Text>
          </IconLine>
        </Grid>
      </ModalHeader>

      <ModalBody padding={modalBodyPadding}>
        <Flex flexDirection={`column`} minHeight={`20rem`}>
          {renderCartContent()}
        </Flex>
      </ModalBody>

      <ModalFooter
        shadow={`medium`}
        padding={{ vertical: `medium`, horizontal: `large` }}
        bgColor={modalFooterBg}
      >
        {renderFooterContent()}
      </ModalFooter>
    </Modal>
  )
}
